import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom';
import { Color, MODAL_FOOTER_BUTTON_SIZE, t } from '@bms2/shared';
import { Button } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { ModalCancel, ModalShell } from '../../components/modals/ModalShell';
import styles from './settings.module.css';

/**
 * A typed value on a Settings page is not dropped silently (decision 2781; T1612).
 *
 * 🔒 A card holding a value typed and not saved registers here by its name. A
 * navigation inside the application that would leave the page, by the side
 * menu, a link or the browser's Back, is held and the application's own window
 * asks first: it names each card and offers Save, Discard and Stay. The
 * browser's own close or reload gets the browser's own leave prompt, because a
 * page cannot draw a window over its own unloading.
 *
 * 🔨 Never `confirm()`: the question is drawn with the Modal shell, so it
 * carries the focus return and the stacking every other window carries.
 * 🔨 A move inside the page, to one of its own section anchors, is not a leave.
 */
export interface UnsavedCard {
  /** The card's name as the person reads it: the page's title, or the list's own name. */
  name: string;
  /** Writes the card's typed values; answers null when they are saved, or the refusal's sentence. */
  save: () => Promise<string | null>;
  /** Drops the typed values, back to what was read. */
  discard: () => void;
}

interface Registry {
  set: (id: string, card: { current: UnsavedCard } | null) => void;
}

const UnsavedCards = createContext<Registry | null>(null);

/** The name a card takes when it carries none of its own: the nearest Settings page's or section's title. */
export const CardName = createContext<string | null>(null);

/**
 * A card's part in the leave question: while `dirty` is true it is named in the
 * question, and the question's Save and Discard reach it through `save` and
 * `discard`. Outside a Settings page nothing is registered and nothing asks.
 */
export function useUnsavedCard(dirty: boolean, card: UnsavedCard): void {
  const registry = useContext(UnsavedCards);
  const id = useId();
  const latest = useRef(card);
  // The newest save and discard, read when the question is answered rather than when the card registered.
  useEffect(() => {
    latest.current = card;
  });
  useEffect(() => {
    if (!registry) return undefined;
    registry.set(id, dirty ? latest : null);
    return () => registry.set(id, null);
  }, [registry, id, dirty]);
}

/** The holder every Settings page stands inside: it keeps the unsaved cards and asks before the page is left. */
export function LeaveGuard({ children }: { children: ReactNode }) {
  const [cards, setCards] = useState<ReadonlyMap<string, { current: UnsavedCard }>>(new Map());
  const set = useCallback((id: string, card: { current: UnsavedCard } | null) => {
    setCards((was) => {
      if (card === null && !was.has(id)) return was;
      const next = new Map(was);
      if (card === null) next.delete(id);
      else next.set(id, card);
      return next;
    });
  }, []);
  const registry = useMemo<Registry>(() => ({ set }), [set]);
  const dirty = cards.size > 0;

  // 🔒 The browser's own close and reload: the browser asks in its own words, which no page may replace.
  useEffect(() => {
    if (!dirty) return undefined;
    const hold = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // The older browsers ask only when the event carries a return value.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', hold);
    return () => window.removeEventListener('beforeunload', hold);
  }, [dirty]);

  // A navigation can be held only under the application's own router; a page drawn alone, as a test draws it, has none to hold.
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  return (
    <UnsavedCards.Provider value={registry}>
      {children}
      {dataRouter ? <LeaveQuestion cards={cards} /> : null}
    </UnsavedCards.Provider>
  );
}

/** The window that asks before a page with a typed value is left. */
function LeaveQuestion({ cards }: { cards: ReadonlyMap<string, { current: UnsavedCard }> }) {
  const dirty = cards.size > 0;
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const blocked = blocker.state === 'blocked';
  if (!blocked) return null;
  const listed = [...cards.values()].map((card) => card.current);
  // Two cards of one page can carry the page's own name; the question names each place once.
  const names = [...new Set(listed.map((card) => card.name))];
  const stay = () => {
    setRefusal(null);
    blocker.reset?.();
  };
  const discard = () => {
    for (const card of listed) card.discard();
    blocker.proceed?.();
  };
  const save = async () => {
    setBusy(true);
    setRefusal(null);
    const refusals: string[] = [];
    for (const card of listed) {
      const refused = await card.save();
      if (refused !== null) refusals.push(`${card.name}: ${refused}`);
    }
    setBusy(false);
    if (refusals.length > 0) setRefusal(refusals.join(' '));
    else blocker.proceed?.();
  };
  return (
    <ModalShell
      open
      title={t('settings.leave.title')}
      onCancel={stay}
      held={busy}
      dataModal="leave-unsaved"
      footer={(
        <>
          <ModalCancel label={t('settings.leave.stay')} onCancel={stay} disabled={busy} />
          <Button size={MODAL_FOOTER_BUTTON_SIZE} text={[t('settings.leave.discard')]} actions={[discard]} disabled={busy} disabledReason={t('settings.leave.saving')} />
          <Button size={MODAL_FOOTER_BUTTON_SIZE} color={Color.Navy} text={[t('settings.leave.save')]} actions={[() => void save()]} disabled={busy} disabledReason={t('settings.leave.saving')} />
        </>
      )}
    >
      <p className={styles.hint}>{t('settings.leave.body')}</p>
      <ul className={styles.leaveCards} data-leave-cards={names.length}>
        {names.map((name) => <li key={name} data-leave-card={name}>{name}</li>)}
      </ul>
      {refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
    </ModalShell>
  );
}
